import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../AuthContext'; // adjust path if needed
import { format, parseISO, set, subDays } from 'date-fns';
import { CalendarIcon, Download, Loader2, RefreshCw, Search, } from 'lucide-react';
import { Layout } from '../Layout';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from '../ui/table';
import { PaginationCustom } from '../ui/pagination-custom';
import { Popover, PopoverContent, PopoverTrigger } from '@radix-ui/react-popover';
import { Calendar } from '../ui/calendar';
import { toast } from 'sonner';

type AuditRecord = {
  id: string | number;
  timestamp: string; // ISO string
  username: string;
  role: string;
  event: string;
  activity: string;
  ip?: string;
  [key: string]: any;
};


export function SystemAuditLog() {
  const { token } = useAuth();

  const [items, setItems] = useState<AuditRecord[]>([]);
  const [allRole, setAllRole] = useState([])
  const [allEvent, setAllEvent] = useState<string[]>([]);

  // States
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isRolLoading, setIsRolLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationData, setPaginationData] = useState<any>();
  const itemsPerPage = 15;
  const totalItems = paginationData?.total || items.length;
  const totalPages =
    paginationData?.pages || Math.ceil((items.length || 0) / itemsPerPage);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [role, setRole] = useState("");
  const [eventFilter, setEventFilter] = useState("");


  // Date Range
  const [appliedRange, setAppliedRange] = useState<{ from?: Date; to?: Date }>({
    from: subDays(new Date(), 7),
    to: new Date(),
  });
  const [tempRange, setTempRange] = useState<{ from?: Date; to?: Date }>({
    from: subDays(new Date(), 7),
    to: new Date(),
  });
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Selected row
  const [selected, setSelected] = useState<AuditRecord | null>(null);

  //Reset Filter
  const resetFilters = () => {
    setSearchTerm('');
    setRole('');
    setEventFilter('');
    setAppliedRange({ from: subDays(new Date(), 7), to: new Date() });
    setTempRange({ from: subDays(new Date(), 7), to: new Date() });
  };
  // ----------------- Fetch Logs -----------------
  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(itemsPerPage),
      });

      if (appliedRange.from)
        params.append("fromDate", format(appliedRange.from, "yyyy-MM-dd"));
      if (appliedRange.to)
        params.append("toDate", format(appliedRange.to, "yyyy-MM-dd"));
      if (searchTerm) params.append("search", searchTerm);
      if (role) params.append("role", role);
      if (eventFilter) params.append("event", eventFilter);

      const res = await fetch(
        `${import.meta.env.VITE_API_URL}users/activity/logs?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "Failed");

      const mapped = data.results.map((r: any) => ({
        id: r.id,
        timestamp: r.createdAt,
        username: r.user?.userName || "-",
        role: r.user?.role || "-",
        event: r.resource,
        activity: r.action || "",
      }));

      setItems(mapped);
      setPaginationData(data.pagination);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ----------------- Fetch Events -----------------
  const fetchEvents = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}users/activity/log/events`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Request failed");

      setAllEvent(data?.events || []);
    } catch (error: any) {
      console.error("Event fetch failed:", error.message);
    }
  };

  // ----------------- Fetch Roles -----------------
  const fetchAllRoles = async () => {
    if (allRole.length > 0) return;
    try {
      setIsRolLoading(true);
      const response = await fetch(`${import.meta.env.VITE_API_URL}roles`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error("Failed to fetch roles");

      const data = await response.json();
      const names = data.roles.map((role: any) => role.displayName);
      setAllRole(names);
    } catch (error) {
      console.error("Error fetching roles:", error);
    } finally {
      setIsRolLoading(false);
    }
  };

  // ----------------- Effects -----------------
  useEffect(() => {
    fetchLogs();
  }, [currentPage, itemsPerPage, appliedRange, searchTerm, role, eventFilter, token]);


  useEffect(() => {
    fetchEvents();
  }, []);
  // ----------------- Utils -----------------
  const formatDate = (date: Date | null) =>
    date ? format(date, "MMM dd, yyyy") : "";

  // ----------------- Download -----------------
  const downloadAuditLogsData = async () => {
    setIsDownloading(true);

    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(itemsPerPage),
      });

      if (appliedRange.from)
        params.append("fromDate", format(appliedRange.from, "yyyy-MM-dd"));
      if (appliedRange.to)
        params.append("toDate", format(appliedRange.to, "yyyy-MM-dd"));
      if (searchTerm) params.append("search", searchTerm);
      if (role) params.append("role", role);
      if (eventFilter) params.append("event", eventFilter);
      params.append("download", "true");

      const res = await fetch(
        `${import.meta.env.VITE_API_URL}users/activity/logs?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const blob = await res.blob();
      const contentDisposition = res.headers.get("content-disposition");

      let filename = `Audit-logs_${format(new Date(), "yyyy-MM-dd")}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e?.message);
    } finally {
      setIsDownloading(false);
    }
  };


  return (
    <Layout>
      <div className="p-8 mx-auto mb-8">
        {/* Header */}
        <div className="mb-6 ms-1">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h1 className="font-inter font-semibold text-2xl mb-2">
              Activity Logs
            </h1>
          </div>
        </div>

        {/* Filter bar */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-wrap lg:flex-nowrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search by username..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Select value={role} onValueChange={setRole} onOpenChange={(open) => {
                  if (open) fetchAllRoles();
                }}>
                  <SelectTrigger className="w-auto">
                    <SelectValue placeholder="All Roles" />
                  </SelectTrigger>
                  <SelectContent className='max-h-80 overflow-y-auto'>
                    {isRolLoading ? (
                      <div className="flex items-center justify-center p-4">
                        <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                      </div>
                    ) : (
                      allRole?.map((r) => (
                        <SelectItem key={r} value={r}>
                          {r}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>

                <Select value={eventFilter} onValueChange={setEventFilter}>
                  <SelectTrigger className="w-auto">
                    <SelectValue placeholder="All Events" />
                  </SelectTrigger>
                  <SelectContent className='max-h-80 overflow-y-auto'>
                    {allEvent.map((ev) => (
                      <SelectItem key={ev} value={ev}>
                        {ev}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button variant="outline" className="flex gap-2 items-center" onClick={() => setShowDatePicker(!showDatePicker)}>
                  <CalendarIcon className="w-4 h-4 text-gray-400" />
                  <p className="text-sm text-gray-600">
                    {appliedRange.from && appliedRange.to
                      ? `${formatDate(appliedRange.from)} - ${formatDate(appliedRange.to)}`
                      : "Select date range"}
                  </p>
                </Button>

                {/* Custom Date Picker Popover */}
                <Popover
                  open={showDatePicker}

                >
                  <PopoverTrigger asChild>
                    <div></div>
                  </PopoverTrigger>
                  <PopoverContent
                    sideOffset={5}
                    className="z-50 w-auto p-0 bg-white shadow-lg border rounded"
                    align="end"
                  >
                    <div className="p-4">
                      <div className="text-sm font-medium mb-2">
                        Select Date Range
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-muted-foreground mb-2">
                            From
                          </div>
                          <Calendar
                            className="scale-90"
                            classNames={{
                              day: "size-6 p-0 text-xs",      // smaller day cells
                              head_cell: "w-6 text-[10px]",   // smaller header
                              caption_label: "text-xs",       // smaller month label
                            }}
                            mode="single"
                            selected={tempRange.from}
                            onSelect={(date) => date && setTempRange((prev) => ({ ...prev, from: date }))}
                            toDate={new Date()}
                            initialFocus
                          />
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-2">
                            To
                          </div>
                          <Calendar
                            className="scale-90"
                            classNames={{
                              day: "size-6 p-0 text-xs",      // smaller day cells
                              head_cell: "w-6 text-[10px]",   // smaller header
                              caption_label: "text-xs",       // smaller month label
                            }}
                            mode="single"
                            selected={tempRange.to}
                            onSelect={(date) => date && setTempRange((prev) => ({ ...prev, to: date }))}
                            toDate={new Date()}

                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 mt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowDatePicker(false);
                            setTempRange({
                              from: subDays(new Date(), 7),
                              to: new Date(),
                            })
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            if (tempRange.from && tempRange.to && tempRange.from > tempRange.to) {
                              toast.error("To Date must be later than the From Date.")

                              return;
                            };
                            setAppliedRange(tempRange); setShowDatePicker(false)
                          }}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                <Button
                  variant="outline"
                  onClick={resetFilters}
                  className="font-inter font-semibold text-[14px] leading-[20px] tracking-normal text-[#2C3E5D]"
                >
                  <RefreshCw className="w-4 h-4 mr-2 text-[#2C3E5D]" />
                  Reset
                </Button>

                <Button
                  onClick={downloadAuditLogsData}
                  className="inline-flex items-center gap-2 text-white px-4 py-2 rounded shadow transition transform duration-150 active:scale-95"
                >
                  {isDownloading ? (<>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Downloading </>) : (
                    <>
                      <Download className="w-4 h-4" />
                      Download
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          {/* <CardContent> */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px] font-inter font-semibold text-[14px] uppercase text-[#758195] ps-5 ">
                    Timestamp
                  </TableHead>
                  <TableHead className="w-[200px] font-inter font-semibold text-[14px] uppercase text-[#758195] text-center">
                    Username
                  </TableHead>
                  <TableHead className="w-[300px] font-inter font-semibold text-[14px] uppercase text-[#758195] text-center">
                    Role
                  </TableHead>
                  <TableHead className="w-[100px] font-inter font-semibold text-[14px] uppercase text-[#758195] text-center">
                    Event
                  </TableHead>
                  <TableHead className="w-[100px] font-inter font-semibold text-[14px] uppercase text-[#758195] text-center">
                    Activity
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center p-6">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : items?.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="p-12 text-center text-gray-600"
                    >
                      No logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  items?.map((row) => (
                    <TableRow
                      key={row.id}
                      onClick={() => setSelected(row)}
                    >
                      <TableCell className="px-4 py-4 text-sm  text-gray-600">
                        {format(
                          parseISO(row.timestamp),
                          'MMM dd yyyy hh:mmaaa'
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-4 text-sm text-gray-600 text-center">
                        {row.username}
                      </TableCell>
                      <TableCell className="px-4 py-4 text-sm text-gray-600 text-center">
                        {row.role}
                      </TableCell>
                      <TableCell className="px-4 py-4 text-sm text-gray-600 text-center">
                        {row.event}
                      </TableCell>
                      <TableCell className="px-4 py-4 text-sm text-gray-600 text-center">
                        {row.activity}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>


        <div className="mt-4 flex justify-center">
          <PaginationCustom
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(newItemsPerPage) => {
              setCurrentPage(1);
            }}
          />
        </div>
        {error && <div className="mt-4 text-red-600">{error}</div>}
      </div>
    </Layout>
  );
}
